/* eslint-disable import/no-cycle */
import {useTranslation} from 'react-i18next';
import {Tabs} from 'antd';
import {
  faBoxes,
  faBook,
  faCheck,
  faEnvelope,
  faExclamationTriangle,
  faFileAlt,
  faInfoCircle,
  faPuzzlePiece,
  faShoppingCart,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import {TabTitle} from '../shared/layouts/TabTitle';
import OperationComponentTable from './OperationComponentTable';
import OrderMaterialTable from './OrderMaterialTable';
import OrderDetailsEventLog from './OrderDetailsEventLog';
import OrderDetailsGeneral from './OrderDetailsGeneral';
import OrderDetailsDocuments from './OrderDetailsDocuments';
import OrderDetailsShiftBook from './OrderDetailsShiftBook';
import OrderMessageTabPane from './OrderMessageTabPane';
import OrderDetailsInterruptions from './OrderDetailsInterruptions';
import Empty from '../shared/empty/Empty';
import InspectionTaskOverviewByOperation from '../inspectionTask/overview/InspectionTaskOverviewByOperation';
import InspectionTaskPendingModal from '../inspectionTask/execution/InspectionTaskExecutionModal';
import ModelPropertyGroups from '../shared/descriptions/ModelPropertyGroups';
import {iconElements} from '../../utils/icon';
import useInspectionTaskExecutionModal from '../../hooks/useInspectionTaskExecutionModal';
import OrderDetailsYields from './OrderDetailsYields';
import appConfig from '../../utils/appConfig';

export const OrderDetails = ({
  operations,
  widgetConfig,
}) => {
  const {t} = useTranslation();
  const generalTabOrderProperties = widgetConfig.getGeneralTabOrderProperties();
  const generalTabOperationProperties = widgetConfig.getGeneralTabOperationProperties();

  const {open: selectPendingTask, inspectionTaskExecutionModalProps} = useInspectionTaskExecutionModal();

  if (!operations.length) {
    return <Empty/>;
  }

  const isOverall = operations.length > 1;

  return (
    <Tabs>
      {widgetConfig.getModalTabsSetting('modalTabs')?.map((tab) => {
        switch (tab) {
          case 'tabGeneral':
            return (
              <Tabs.TabPane
                tab={<TabTitle faIcon={faInfoCircle} title={t('orderWidget.tab.general')}/>}
                key="tab-general"
              >
                <OrderDetailsGeneral
                  operations={operations}
                  uomTypes={widgetConfig.getGeneralTabSettings('modalTabGeneral.unitOfMeasurementTypes')}
                  showOrderFirst={widgetConfig.getGeneralTabSettings('modalTabGeneral.showOrderFirst')}
                  showPhaseMachineHours={widgetConfig.getGeneralTabSettings('modalTabGeneral.showPhaseMachineHours')}
                  orderProperties={generalTabOrderProperties}
                  operationProperties={generalTabOperationProperties}
                />
              </Tabs.TabPane>
            );
          case 'tabCustom':
            return (
              <>
                {widgetConfig.getCustomTabs().map((customTab, index) => (
                  <Tabs.TabPane
                    tab={<TabTitle faIcon={iconElements[customTab.icon]} title={customTab.title}/>}
                    key={`tab-custom-${index.toString()}`}
                    disabled={isOverall}
                  >
                    {isOverall ? null : (
                      <ModelPropertyGroups
                        column={customTab.columnCount}
                        groups={customTab.groups}
                        model={operations[0]}
                        hideEmpty={!customTab.showEmpty}
                      />
                    )}
                  </Tabs.TabPane>
                ))}
              </>
            );
          case 'tabComponents':
            return (
              <Tabs.TabPane
                tab={<TabTitle faIcon={faPuzzlePiece} title={t('orderWidget.tab.components')}/>}
                key="tab-components"
                disabled={isOverall}
              >
                {isOverall ? null : (
                  <OperationComponentTable
                    columns={widgetConfig.getComponentsTableColumns()}
                    groupComponents={widgetConfig.getComponentsTableSettings('modalTabComponents.groupComponents')}
                    operationId={operations[0]?.id}
                  />
                )}
              </Tabs.TabPane>
            );
          case 'tabOrderMaterials':
            return (
              <Tabs.TabPane
                tab={<TabTitle faIcon={faShoppingCart} title={t('orderWidget.tab.orderMaterials')}/>}
                key="tab-order-materials"
              >
                <OrderMaterialTable
                  columns={widgetConfig.getComponentsTableColumns()}
                  orderId={operations[0]?.order?.id}
                />
              </Tabs.TabPane>
            );
          case 'tabYields':
            if (appConfig.modules.enableYields) {
              return (
                <Tabs.TabPane
                  tab={<TabTitle faIcon={faBoxes} title={t('orderWidget.tab.yields')}/>}
                  key="tab-yields"
                >
                  <OrderDetailsYields operations={operations}/>
                </Tabs.TabPane>
              );
            }
            return null;
          case 'tabDocuments':
            return (
              <Tabs.TabPane tab={<TabTitle faIcon={faFileAlt} title={t('document.model.other')}/>} key="tab-documents">
                <OrderDetailsDocuments operations={operations}/>
              </Tabs.TabPane>
            );
          case 'tabMessages':
            return (
              <Tabs.TabPane tab={<TabTitle faIcon={faEnvelope} title={t('message.model.other')}/>} key="tab-messages">
                <OrderMessageTabPane operations={operations}/>
              </Tabs.TabPane>
            );
          case 'tabShiftBook':
            return (
              <Tabs.TabPane tab={<TabTitle faIcon={faBook} title={t('shiftBook.model.other')}/>} key="tab-shiftBook">
                <OrderDetailsShiftBook operation={operations[0]}/>
              </Tabs.TabPane>
            );
          case 'tabTasks':
            return (
              <Tabs.TabPane
                tab={<TabTitle faIcon={faCheck} title={t('task.model.other')}/>}
                key="tab-tasks"
                disabled={isOverall}
              >
                { isOverall ? null : ([
                  <InspectionTaskOverviewByOperation operation={operations[0]} selectPendingTask={selectPendingTask}/>,
                  <InspectionTaskPendingModal
                    {...inspectionTaskExecutionModalProps}
                  />,
                ])}
              </Tabs.TabPane>
            );
          case 'tabInterruptions':
            return (
              <Tabs.TabPane
                tab={<TabTitle faIcon={faExclamationTriangle} title={t('orderWidget.tab.interruptions')}/>}
                key="tab-interruptions"
              >
                <OrderDetailsInterruptions
                  operations={operations}
                  columns={widgetConfig.getInterruptionsTabSetting('modalTabInterruption.columns')}
                  sortAttribute={widgetConfig.getInterruptionsTabSetting('modalTabInterruption.sortAttribute')}
                  sortOrder={widgetConfig.getInterruptionsTabSetting('modalTabInterruption.sortOrder')}
                />
              </Tabs.TabPane>
            );
          case 'tabEventLog':
            return (
              <Tabs.TabPane tab={<TabTitle faIcon={faUndo} title={t('eventLog.model.other')}/>} key="tab-eventLog">
                <OrderDetailsEventLog operation={operations[0]} filterWorkplace={operations.length === 1}/>
              </Tabs.TabPane>
            );
          default:
            return null;
        }
      })}
    </Tabs>
  );
};
